import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { FitmentPrefill } from '@/components/landing/fitment-lab'

import { useEffect, useId, useState } from 'react'
import { CircleCheck, LoaderCircle, Send, TriangleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FINISHES } from '@/constants/finishes'
import { FITMENT_PREFILL_EVENT } from '@/components/landing/fitment-lab'

interface FormValues {
    name: string
    email: string
    phone: string
    vehicle: string
    diameter: string
    finish: string
    fitmentSpec: string
    message: string
    company: string
}

type FieldErrors = Partial<Record<keyof FormValues, string>>
type Status = 'idle' | 'submitting' | 'success' | 'error'

const INITIAL: FormValues = {
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    diameter: '',
    finish: '',
    fitmentSpec: '',
    message: '',
    company: ''
}

const DIAMETERS = [
    { value: '19', label: '19"' },
    { value: '20', label: '20"' },
    { value: '21', label: '21"' },
    { value: '22', label: '22"' },
    { value: '23', label: '23"' },
    { value: 'unsure', label: 'Not sure' }
]

const FINISH_OPTIONS = [
    ...FINISHES.map((finish) => ({ value: finish.key as string, label: finish.name, swatch: finish.swatch })),
    { value: 'bespoke', label: 'Bespoke finish', swatch: 'conic-gradient(from 90deg, #ffd700, #8a95a5, #c8ac72, #1d2128, #ffd700)' }
]

const EMAIL_PATTERN = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}
    if (values.name.trim().length < 2) errors.name = 'Please enter your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (values.vehicle.trim().length < 2) errors.vehicle = 'Tell us which car the set is for.'
    return errors
}

const inputClass = (invalid: boolean): string => cn(
    'mt-2 w-full rounded-sm border bg-obsidian px-4 py-3 text-[15px] text-white placeholder:text-titanium/70 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50',
    invalid ? 'border-destructive' : 'border-white/10 hover:border-white/20 focus:border-gold/70'
)

const CommissionForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(INITIAL)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [submittedName, setSubmittedName] = useState('')
    const uid = useId()

    useEffect(() => {
        const handler = (event: Event): void => {
            const detail = (event as CustomEvent<FitmentPrefill>).detail
            if (!detail) return
            setValues((current) => ({ ...current, diameter: detail.diameter, fitmentSpec: detail.spec }))
            setStatus((current) => (current === 'success' ? 'idle' : current))
        }
        window.addEventListener(FITMENT_PREFILL_EVENT, handler)
        return () => window.removeEventListener(FITMENT_PREFILL_EVENT, handler)
    }, [])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const value = event.target.value
        setValues((current) => ({ ...current, [field]: value }))
        if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }))
    }

    const choose = (field: 'diameter' | 'finish', value: string): void => {
        setValues((current) => ({ ...current, [field]: current[field] === value ? '' : value }))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        const found = validate(values)
        setErrors(found)
        if (Object.keys(found).length > 0) {
            setStatus('error')
            setServerMessage('Please check the highlighted fields.')
            const first = Object.keys(found)[0]
            document.getElementById(`${uid}-${first}`)?.focus()
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/fitment-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })
            const data = (await response.json().catch(() => ({}))) as { ok?: boolean, message?: string, errors?: FieldErrors }

            if (!response.ok || !data.ok) {
                setErrors(data.errors ?? {})
                setStatus('error')
                setServerMessage(data.message ?? 'We could not send your request just now. Please try again in a moment.')
                return
            }

            setSubmittedName(values.name.trim().split(' ')[0] ?? '')
            setValues(INITIAL)
            setErrors({})
            setStatus('success')
        }
        catch {
            setStatus('error')
            setServerMessage('We could not reach the atelier. Check your connection and try again.')
        }
    }

    if (status === 'success') {
        return (
            <div className='flex flex-col items-start rounded-xl border border-gold/30 bg-slate-deep p-8 sm:p-10' role='status'>
                <CircleCheck className='size-10 text-gold' aria-hidden='true' />
                <h3 className='mt-6 font-heading text-xl font-bold text-white'>
                    Request received
                </h3>
                <p className='mt-4 text-base leading-relaxed text-titanium-light'>
                    Thank you{submittedName ? `, ${submittedName}` : ''}. Your fitment request is with the atelier, and we will reply personally by email to plan the next step.
                </p>
                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-8 font-mono text-[11px] uppercase tracking-[0.24em] text-gold underline-offset-8 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                >
                    Send another request
                </button>
            </div>
        )
    }

    const fieldError = (field: keyof FormValues): ReactNode => errors[field] && (
        <p id={`${uid}-${field}-error`} className='mt-2 text-sm text-destructive'>
            {errors[field]}
        </p>
    )

    return (
        <form noValidate onSubmit={handleSubmit} className='rounded-xl border border-white/5 bg-slate-deep/90 p-6 sm:p-10' aria-describedby={`${uid}-status`}>
            <div className='grid gap-6 sm:grid-cols-2'>
                <div>
                    <label htmlFor={`${uid}-name`} className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                        Full name
                    </label>
                    <input
                        id={`${uid}-name`}
                        name='name'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? `${uid}-name-error` : undefined}
                        className={inputClass(Boolean(errors.name))}
                        required
                    />
                    {fieldError('name')}
                </div>
                <div>
                    <label htmlFor={`${uid}-email`} className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                        Email
                    </label>
                    <input
                        id={`${uid}-email`}
                        name='email'
                        type='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? `${uid}-email-error` : undefined}
                        className={inputClass(Boolean(errors.email))}
                        required
                    />
                    {fieldError('email')}
                </div>
                <div>
                    <label htmlFor={`${uid}-phone`} className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                        Phone <span className='normal-case tracking-normal text-titanium'>(optional)</span>
                    </label>
                    <input
                        id={`${uid}-phone`}
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        value={values.phone}
                        onChange={update('phone')}
                        className={inputClass(false)}
                    />
                </div>
                <div>
                    <label htmlFor={`${uid}-vehicle`} className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                        Vehicle
                    </label>
                    <input
                        id={`${uid}-vehicle`}
                        name='vehicle'
                        placeholder='Make, model and year'
                        value={values.vehicle}
                        onChange={update('vehicle')}
                        aria-invalid={Boolean(errors.vehicle)}
                        aria-describedby={errors.vehicle ? `${uid}-vehicle-error` : undefined}
                        className={inputClass(Boolean(errors.vehicle))}
                        required
                    />
                    {fieldError('vehicle')}
                </div>
            </div>

            <fieldset className='mt-8'>
                <legend className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                    Diameter
                </legend>
                <div className='mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6'>
                    {DIAMETERS.map((option) => (
                        <button
                            key={option.value}
                            type='button'
                            onClick={() => choose('diameter', option.value)}
                            aria-pressed={values.diameter === option.value}
                            className={cn(
                                'rounded-sm border px-2 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
                                values.diameter === option.value ? 'border-gold bg-gold text-obsidian' : 'border-white/10 text-white hover:border-gold/60'
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </fieldset>

            <fieldset className='mt-8'>
                <legend className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                    Finish
                </legend>
                <div className='mt-3 grid gap-2 sm:grid-cols-2'>
                    {FINISH_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type='button'
                            onClick={() => choose('finish', option.value)}
                            aria-pressed={values.finish === option.value}
                            className={cn(
                                'flex items-center gap-3 rounded-sm border px-4 py-3 text-left text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
                                values.finish === option.value ? 'border-gold bg-gold/10 text-white' : 'border-white/10 text-titanium-light hover:border-gold/60'
                            )}
                        >
                            <span className='size-5 shrink-0 rounded-full border border-white/20' style={{ background: option.swatch }} aria-hidden='true' />
                            {option.label}
                        </button>
                    ))}
                </div>
            </fieldset>

            {values.fitmentSpec && (
                <div className='mt-8 flex items-center justify-between gap-4 rounded-sm border border-gold/30 bg-gold/5 px-4 py-3'>
                    <p className='text-sm text-titanium-light'>
                        From the fitment lab: <span className='font-semibold text-gold'>{values.fitmentSpec}</span>
                    </p>
                    <button
                        type='button'
                        onClick={() => setValues((current) => ({ ...current, fitmentSpec: '' }))}
                        aria-label='Remove the fitment lab specification'
                        className='text-titanium transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-gold'
                    >
                        <X className='size-4' />
                    </button>
                </div>
            )}

            <div className='mt-8'>
                <label htmlFor={`${uid}-message`} className='font-mono text-[11px] uppercase tracking-[0.22em] text-titanium-light'>
                    Tell us about the build <span className='normal-case tracking-normal text-titanium'>(optional)</span>
                </label>
                <textarea
                    id={`${uid}-message`}
                    name='message'
                    rows={4}
                    placeholder='Current wheels, brakes, the stance you are after, any finish ideas'
                    value={values.message}
                    onChange={update('message')}
                    className={cn(inputClass(false), 'resize-y')}
                />
            </div>

            <div className='absolute -left-[9999px] h-0 w-0 overflow-hidden' aria-hidden='true'>
                <label htmlFor={`${uid}-company`}>Company</label>
                <input id={`${uid}-company`} name='company' tabIndex={-1} autoComplete='off' value={values.company} onChange={update('company')} />
            </div>

            <div id={`${uid}-status`} aria-live='polite'>
                {status === 'error' && serverMessage && (
                    <p className='mt-6 flex items-start gap-3 rounded-sm border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-white' role='alert'>
                        <TriangleAlert className='mt-0.5 size-4 shrink-0 text-destructive' aria-hidden='true' />
                        {serverMessage}
                    </p>
                )}
            </div>

            <button
                type='submit'
                disabled={status === 'submitting'}
                className='gold-sheen mt-8 inline-flex w-full items-center justify-center gap-3 rounded-sm bg-gold px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.26em] text-obsidian transition-colors hover:bg-[#ffe34d] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold disabled:cursor-wait disabled:opacity-80'
            >
                {status === 'submitting' ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' /> : <Send className='size-4' aria-hidden='true' />}
                {status === 'submitting' ? 'Sending to the atelier' : 'Request my fitment'}
            </button>

            <p className='mt-4 text-center text-xs leading-relaxed text-titanium'>
                We use your details only to reply to this request.
            </p>
        </form>
    )
}

export default CommissionForm
